import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, errorMessage } from './api';
import type { Board, UserSummary, WorkspaceSummary } from './types';

import { tr } from '@/i18n';
export type BoardsView = 'all' | 'recent' | 'favorites' | 'shared' | 'trash' | 'templates';

export interface BoardsQuery {
  view: BoardsView;
  workspaceId?: string;
  projectId?: string;
  q?: string;
  sort?: 'updated' | 'name' | 'created';
}

export function useBoards(params: BoardsQuery, enabled = true) {
  return useQuery({
    queryKey: ['boards', params],
    queryFn: () => api<{ boards: Board[] }>('/api/boards', { query: { ...params } }).then((r) => r.boards),
    enabled,
    staleTime: 10_000,
  });
}

export interface Project {
  id: string;
  name: string;
  workspaceId: string;
}

export function useProjects(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['projects', workspaceId],
    queryFn: () => api<{ projects: Project[] }>(`/api/workspaces/${workspaceId}/projects`).then((r) => r.projects),
    enabled: !!workspaceId,
  });
}

export interface WorkspaceMember extends UserSummary {
  email?: string;
  role: WorkspaceSummary['role'];
  joinedAt: number;
}

export interface WorkspaceDetails {
  workspace: {
    id: string;
    name: string;
    personal: boolean;
    ownerId: string;
    role: WorkspaceSummary['role'];
    boardCount: number;
    inviteLink: string | null;
    inviteRole: 'admin' | 'member' | 'guest';
    inviteEnabled: boolean;
  };
  members: WorkspaceMember[];
  invites: Array<{ id: string; email: string; role: string; boardId: string | null; createdAt: number; expiresAt: number }>;
}

export function useWorkspace(id: string | undefined) {
  return useQuery({ queryKey: ['workspace', id], queryFn: () => api<WorkspaceDetails>(`/api/workspaces/${id}`), enabled: !!id });
}

/** Мутации досок с инвалидацией списков и тостами об ошибках. */
export function useBoardActions() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['boards'] });
  };
  const onError = (err: unknown) => toast.error(errorMessage(err));
  return {
    rename: useMutation({ mutationFn: ({ id, name }: { id: string; name: string }) => api(`/api/boards/${id}`, { method: 'PATCH', body: { name } }), onSuccess: refresh, onError }),
    move: useMutation({
      mutationFn: ({ id, projectId }: { id: string; projectId: string | null }) => api(`/api/boards/${id}`, { method: 'PATCH', body: { projectId } }),
      onSuccess: () => {
        refresh();
        toast.success(tr("Доска перемещена"));
      },
      onError,
    }),
    favorite: useMutation({
      mutationFn: ({ id, on }: { id: string; on: boolean }) => api(`/api/boards/${id}/favorite`, { method: on ? 'PUT' : 'DELETE' }),
      onSuccess: refresh,
      onError,
    }),
    duplicate: useMutation({
      mutationFn: ({ id, workspaceId }: { id: string; workspaceId?: string }) => api<{ board: Board }>(`/api/boards/${id}/duplicate`, { body: { workspaceId } }),
      onSuccess: () => {
        refresh();
        toast.success(tr("Копия создана"));
      },
      onError,
    }),
    trash: useMutation({
      mutationFn: (id: string) => api(`/api/boards/${id}`, { method: 'DELETE' }),
      onSuccess: () => {
        refresh();
        toast.success(tr("Доска в корзине — её можно восстановить в течение 30 дней"));
      },
      onError,
    }),
    restore: useMutation({
      mutationFn: (id: string) => api(`/api/boards/${id}/restore`, { method: 'POST' }),
      onSuccess: () => {
        refresh();
        toast.success(tr("Доска восстановлена"));
      },
      onError,
    }),
    purge: useMutation({
      mutationFn: (id: string) => api(`/api/boards/${id}/permanent`, { method: 'DELETE' }),
      onSuccess: () => {
        refresh();
        toast.success(tr("Доска удалена навсегда"));
      },
      onError,
    }),
  };
}
